import React, { useRef } from 'react';

interface GridCellData {
  colspan: number;
  is_admin: boolean;
  position: number;
  rowspan: number;
  style: {
    backgroundColor?: string;
    font?: {
      color?: string;
      family?: string;
      weight?: string;
    };
    height: string;
    textAlign?: string;
    width: string;
  };
  val: string;
}

export interface GridData {
  chunk: string; // JSON string of GridCellData[][]
  col_ratios: string;
  columns: string;
  height: string;
  row_ratios: string;
  rows: string;
}

interface GridQuestionTableProps {
  gridData: GridData | null;
}

const GridQuestionTable = ({ gridData }: GridQuestionTableProps) => {
  const tableRef = useRef<HTMLTableElement>(null);

  const normalizeRatios = (ratios: string[]): number[] => {
    const total = ratios.reduce((sum, ratio) => sum + parseFloat(ratio), 0);
    return ratios.map(ratio => (parseFloat(ratio) / total) * 100);
  };

  // Handles HTML content like line breaks
  const formatCellContent = (val: string): string => val.replace(/\\u003cbr\\u003e/g, '<br>');

  const renderCell = (cellData: GridCellData, rowIndex: number, cellIndex: number) => {
    const { style, colspan, rowspan, is_admin: isAdmin, val } = cellData;

    const cellStyle: React.CSSProperties = {
      backgroundColor: style.backgroundColor || '#ffffff',
      border: '1px solid #ddd',
      height: style.height,
      padding: '8px',
      textAlign: style.textAlign as any || (isAdmin ? 'center' : 'left'),
      verticalAlign: 'top',
      width: style.width,
      ...(style.font && {
        color: style.font.color || '#000000',
        fontFamily: style.font.family,
        fontWeight: style.font.weight || 'normal',
      }),
    };

    if (isAdmin) {
      cellStyle.fontWeight = 'bold';
      cellStyle.color = style.font?.color || '#53318e';
    }

    return (
      <td
        key={`${rowIndex}-${cellIndex}`}
        className={isAdmin ? 'grid_admin_cell' : ''}
        colSpan={colspan}
        rowSpan={rowspan}
        style={cellStyle}
      >
        <div
          className="tw-min-h-4 tw-break-words"
          dangerouslySetInnerHTML={{ __html: formatCellContent(val) }}
        />
      </td>
    );
  };

  const renderTable = () => {
    if (!gridData) return null;

    let cellData: GridCellData[][];
    try {
      cellData = JSON.parse(gridData.chunk);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to parse grid chunk:', err);
      return <div>Error parsing grid data</div>;
    }

    const colRatios = gridData.col_ratios.split(',');
    const normalizedRatios = normalizeRatios(colRatios);

    return (
      <table
        ref={tableRef}
        className="tw-w-full tw-border-collapse tw-table-fixed"
        style={{ minHeight: `${gridData.height}px` }}
      >
        {/* Control row for column widths */}
        <colgroup>
          {normalizedRatios.map((ratio, index) => (
            <col
              key={`ratio-col-${index}`}
              style={{ width: `${ratio}%` }}
            />
          ))}
        </colgroup>
        <tbody>
          {cellData.map((rowData, rowIndex) => (
            <tr key={`cell-data-row-${rowIndex}`}>
              {rowData.map((data, cellIndex) => renderCell(data, rowIndex, cellIndex))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return renderTable();
};

export default GridQuestionTable;
